/* lib/adapters/menus.js — форма данных «Меню» между боевым API и UI прототипа.
 * Backend: decimal-as-string → Number; dish.id == ttk.id; food_cost_pct сервер НЕ отдаёт
 * (derive из cost/selling); style_preset отдельным полем + style-словарь → объединяем в
 * style.preset + остальное; шрифты — slug serif|sans|condensed (не имена гарнитур). */

import { fmt } from '../format.js';

export const num = (v) => (v == null || v === '' ? null : Number(v));

// ── Опции дизайн-вкладки (slug ↔ RU-метка), синхронны бэкенд-энумам ───────────
export const MENU_PRESETS = [
  { value: 'classic', label: 'Классический' },
  { value: 'modern', label: 'Современный' },
  { value: 'compact', label: 'Компактный' },
  { value: 'chalkboard', label: 'Грифельная доска' },
];
export const MENU_FONTS = [
  { value: 'serif', label: 'С засечками' },
  { value: 'sans', label: 'Без засечек' },
  { value: 'condensed', label: 'Узкий' },
];
// slug → реальная гарнитура (для client-side предпросмотра в iframe; бэк рендерит сам).
export const FONT_FAMILY = {
  serif: 'Georgia, "Times New Roman", "PT Serif", serif',
  sans: "'Geist', system-ui, sans-serif",
  condensed: "'Geist', system-ui, sans-serif",
};
const FONT_SLUGS = new Set(['serif', 'sans', 'condensed']);
function normFont(v, fallback) {
  if (FONT_SLUGS.has(v)) return v;
  if (typeof v === 'string' && /serif/i.test(v)) return 'serif';
  if (typeof v === 'string' && v) return 'sans';
  return fallback;
}

// FC% позиции меню сервер не отдаёт — derive из себестоимости и цены продажи (источник — ТТК).
function deriveFc(cost, sell) {
  if (cost == null || sell == null || sell <= 0) return null;
  return Math.round((cost / sell) * 100);
}

// ── Реестр (MenuListSerializer) → форма плитки реестра ────────────────────────
export function fromApiMenuBrief(m) {
  return {
    id: m.id,
    name: m.name,
    status: m.status, // 'draft' | 'active' | 'archived'
    status_label: m.status_label,
    dishes_count: m.dishes_count || 0,
    average_check: num(m.average_check),
    target_food_cost_pct: num(m.target_food_cost_pct),
    average_cost_per_portion: num(m.average_cost_per_portion),
    total_cost: num(m.total_cost),
    cost_status: m.cost_status,
    cost_missing_count: m.cost_missing_count || 0,
  };
}

// ── Detail ────────────────────────────────────────────────────────────────────
function sectionFromApi(s) {
  return { id: s.id, title: s.title || '', subtitle: s.subtitle || '', order_index: s.order_index || 0 };
}

function dishFromApi(d) {
  const cost = num(d.cost_per_portion_rub);
  const sell = num(d.selling_price_rub);
  return {
    id: d.id,            // == ttk.id; UI ждёт строковый/числовой id — оставляем числовым
    ttk_id: d.id,        // членство/раскладка ключуются по ttk_id (== id)
    section_id: d.section_id ?? null,
    title: d.title || '',
    cost_per_portion_rub: cost,
    selling_price_rub: sell,
    output_g: num(d.output_g),
    food_cost_pct: deriveFc(cost, sell),
    in_stop_list: !!d.in_stop_list,
    is_stub: !!d.is_stub,
    cost_status: d.cost_status,
    cost_status_label: d.cost_status_label,
    order_index: d.order_index || 0,
  };
}

// style_preset (отдельное поле) + style (словарь токенов) → единый style с .preset.
// Дефолты рендера (resolve_style на бэке): show_prices/weight=true, kbju/allergens=false.
function styleFromApi(d) {
  const st = (d.style && typeof d.style === 'object') ? d.style : {};
  const bool = (v, def) => (v == null ? def : !!v);
  return {
    preset: d.style_preset || 'classic',
    accent_color: st.accent_color || '#3776FE',
    bg_color: st.bg_color || '#FFFFFF',
    heading_font: normFont(st.heading_font, 'serif'),
    body_font: normFont(st.body_font, 'sans'),
    show_prices: bool(st.show_prices, true),
    show_weight: bool(st.show_weight, true),
    show_kbju: bool(st.show_kbju, false),
    show_allergens: bool(st.show_allergens, false),
    header_text: st.header_text || '',
    footer_text: st.footer_text || '',
  };
}

// MenuDetailSerializer → форма, которую ждёт MenuEditor (sections/dishes/style).
export function fromApiMenuDetail(d) {
  return {
    id: d.id,
    name: d.name || '',
    concept: d.concept || '',
    comments: d.comments || '',
    product_matrix: d.product_matrix || '',
    average_check: num(d.average_check),
    target_food_cost_pct: num(d.target_food_cost_pct),
    status: d.status, // 'draft' | 'active' | 'archived'
    status_label: d.status_label,
    sections: (d.sections || []).map(sectionFromApi),
    dishes: (d.dishes || []).map(dishFromApi),
    style: styleFromApi(d),
    // серверная экономика (число) — read-only зеркала
    average_cost_per_portion: num(d.average_cost_per_portion),
    total_cost: num(d.total_cost),
    cost_status: d.cost_status,
    cost_missing_count: d.cost_missing_count || 0,
  };
}

// Пустой detail для создания нового меню (slug-шрифты).
export function blankMenuDetailReal() {
  return {
    id: null, name: '', concept: '', comments: '', product_matrix: '', average_check: null, target_food_cost_pct: 30, status: 'draft',
    sections: [], dishes: [],
    style: {
      preset: 'classic', accent_color: '#3776FE', bg_color: '#FFFFFF',
      heading_font: 'serif', body_font: 'sans',
      show_prices: true, show_weight: true, show_kbju: false, show_allergens: false,
      header_text: '', footer_text: '',
    },
  };
}

// Локальный menu (UI) → мета/стиль payload для PATCH/POST меню (MenuWriteSerializer).
// Раздел/порядок/стоп-лист и членство — НЕ здесь (reorder + dishes[]).
export function toMenuMetaPayload(menu) {
  const st = menu.style || {};
  return {
    name: (menu.name || '').trim(),
    concept: menu.concept || '',
    comments: menu.comments || '',
    product_matrix: menu.product_matrix || '',
    status: menu.status,
    average_check: menu.average_check ?? null,
    target_food_cost_pct: menu.target_food_cost_pct ?? null,
    style_preset: st.preset || 'classic',
    style: {
      accent_color: st.accent_color || '#3776FE',
      bg_color: st.bg_color || '#FFFFFF',
      heading_font: st.heading_font || 'serif',
      body_font: st.body_font || 'sans',
      show_prices: !!st.show_prices,
      show_weight: !!st.show_weight,
      show_kbju: !!st.show_kbju,
      show_allergens: !!st.show_allergens,
      header_text: st.header_text || '',
      footer_text: st.footer_text || '',
    },
  };
}

// TTK-brief (kind=dish) → строка пикера «Добавить блюда из ТТК».
// Цена продажи — из ТТК (selling_price_rub); не фабрикуем из cost.
export function dishPickerRow(t) {
  const cost = num(t.cost_per_portion_rub);
  const sell = num(t.selling_price_rub);
  return {
    id: t.id,
    label: t.title,
    hint: t.cost_status === 'ok' && cost != null ? `${fmt.money(cost)}/порц` : (cost != null ? `${fmt.money(cost)}/порц · нет части цен` : 'без расчёта'),
    cost,
    sell,
    output_g: num(t.output_g),
    fc: deriveFc(cost, sell),
    stub: t.status === 'stub',
  };
}

